import {
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import type { PaginationState } from "@tanstack/react-table";

import {
    deleteOutputTemplate,
    getOutputTemplateDetail,
    listOutputTemplates,
    renameOutputTemplate,
    replaceOutputTemplate,
    retryOutputTemplateCompile,
    uploadOutputTemplate,
    type CompileStatus,
    type OutputTemplateDetail,
    type OutputTemplateListItem,
    type OutputTemplateUploadConfirm,
    type OutputTemplateVersion,
} from "@/api";

// Only an in-flight compile warrants polling. Every other state is at rest: a
// successful compile auto-serves as `ready`, and the failures wait for a
// re-roll/replace — so polling those would spin every 5s forever without a change.
const POLLING_STATUSES = new Set<CompileStatus>(["pending", "processing"]);

const POLL_INTERVAL_MS = 5000;

const OUTPUT_TEMPLATES_KEY = "output-templates";

export const outputTemplatesQueryKey = (
    search: string,
    pagination: PaginationState
) =>
    [
        OUTPUT_TEMPLATES_KEY,
        "list",
        search,
        pagination.pageIndex,
        pagination.pageSize,
    ] as const;

// Latest-wins: a staged (newer) version's status supersedes the serving one.
export function displayStatus(
    item: OutputTemplateListItem
): CompileStatus | null {
    return item.staged_compile_status ?? item.current_compile_status;
}

export function latestVersion(
    detail: OutputTemplateDetail | undefined
): OutputTemplateVersion | null {
    if (!detail || detail.versions.length === 0) return null;
    return detail.versions.reduce((latest, version) => {
        if (version.created_at !== latest.created_at) {
            return version.created_at > latest.created_at ? version : latest;
        }
        return version.id > latest.id ? version : latest;
    });
}

export function reviewVersion(
    detail: OutputTemplateDetail | undefined
): OutputTemplateVersion | null {
    if (!detail) return null;
    const byPreview = detail.versions.find(
        (version) => version.id === detail.preview?.version_id
    );
    return byPreview ?? latestVersion(detail);
}

export function useOutputTemplates(
    search: string,
    pagination: PaginationState
) {
    return useQuery({
        queryKey: outputTemplatesQueryKey(search, pagination),
        queryFn: () =>
            listOutputTemplates({
                search: search || undefined,
                page: pagination.pageIndex + 1,
                size: pagination.pageSize,
            }),
        // Keep the prior rows on screen while a new search resolves so each
        // debounced keystroke doesn't flash the skeleton.
        placeholderData: keepPreviousData,
        refetchInterval: (query) => {
            if (query.state.status === "error") return false;
            const items = query.state.data?.items ?? [];
            const live = items.some((t) => {
                const status = displayStatus(t);
                return status != null && POLLING_STATUSES.has(status);
            });
            return live ? POLL_INTERVAL_MS : false;
        },
    });
}

export function useRetryCompile() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => retryOutputTemplateCompile(id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [OUTPUT_TEMPLATES_KEY],
            });
        },
    });
}

export interface UploadTemplateVars {
    payload: OutputTemplateUploadConfirm;
}

export function useUploadTemplate() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ payload }: UploadTemplateVars) =>
            uploadOutputTemplate(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [OUTPUT_TEMPLATES_KEY],
            });
        },
    });
}

export function useReplaceTemplate(id: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ payload }: UploadTemplateVars) =>
            replaceOutputTemplate(id, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [OUTPUT_TEMPLATES_KEY],
            });
        },
    });
}

export const outputTemplateDetailQueryKey = (id: number) =>
    [OUTPUT_TEMPLATES_KEY, "detail", id] as const;

export function useOutputTemplateDetail(
    templateId: number | null,
    options?: { enabled?: boolean }
) {
    return useQuery({
        queryKey: outputTemplateDetailQueryKey(templateId ?? -1),
        queryFn: () => getOutputTemplateDetail(templateId!),
        enabled: (options?.enabled ?? true) && templateId != null,
        refetchInterval: (query) => {
            if (query.state.status === "error") return false;
            const versions = query.state.data?.versions ?? [];
            const live = versions.some((v) =>
                POLLING_STATUSES.has(v.compile_status)
            );
            return live ? POLL_INTERVAL_MS : false;
        },
    });
}

export function useRenameTemplate() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, name }: { id: number; name: string }) =>
            renameOutputTemplate(id, name),
        onSuccess: () => {
            // The prefix already matches the detail key, so one invalidate
            // refetches both the list and the open detail.
            queryClient.invalidateQueries({ queryKey: [OUTPUT_TEMPLATES_KEY] });
        },
    });
}

export function useDeleteTemplate() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => deleteOutputTemplate(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [OUTPUT_TEMPLATES_KEY] });
        },
    });
}
